import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Save, Settings, Mail, MessageCircle, FileText, 
  Image, Loader2, CheckCircle, Users, Eye 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';

export default function ConfiguracaoWebinar() {
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data: configs, isLoading } = useQuery({
    queryKey: ['webinar-config'],
    queryFn: () => base44.entities.WebinarConfig.list(),
    initialData: []
  });

  const { data: inscricoes } = useQuery({
    queryKey: ['webinar-inscricoes'],
    queryFn: () => base44.entities.WebinarInscricao.list('-created_date'),
    initialData: []
  });

  const [formData, setFormData] = useState({
    titulo_webinar: "Inscrição - TalkToMe Partners",
    subtitulo: "Seja um Partner TalkToMe e Monetize Implantando a Plataforma!",
    data_evento: "18 e 19 de dezembro de 2025",
    detalhes_treinamento: "Formato: Online Ao Vivo, via Google Meet\nCarga Horária: 12h (6h por dia, das 9h às 16h)\nDatas: 18 e 19 de dezembro de 2025\nDia 1: Foco Estratégico + Comercial + Fundamentos\nDia 2: Foco Técnico + Construção de Projetos",
    assunto_email: "🎉 Inscrição Confirmada - TalkToMe Partners",
    mensagem_email: "<h2>Parabéns! Sua inscrição foi confirmada!</h2><p>Estamos muito felizes em ter você conosco no treinamento TalkToMe Partners.</p><p>Em breve você receberá mais detalhes sobre o acesso ao treinamento.</p><p><strong>Nos vemos lá!</strong></p>",
    mensagem_whatsapp: "🎉 Olá! Sua inscrição no TalkToMe Partners foi confirmada!\n\nEm breve você será adicionado à nossa comunidade oficial no WhatsApp.\n\nLá você receberá:\n✅ Todos os detalhes e links de acesso ao treinamento\n✅ Avisos importantes\n✅ Materiais complementares\n\nNos vemos no treinamento! 🚀",
    logo_url: ""
  });

  useEffect(() => {
    if (configs?.length > 0) {
      setFormData(configs[0]);
    }
  }, [configs]);

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    if (configs?.length > 0) {
      await base44.entities.WebinarConfig.update(configs[0].id, formData);
    } else {
      await base44.entities.WebinarConfig.create(formData);
    }
    queryClient.invalidateQueries({ queryKey: ['webinar-config'] });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Settings className="w-8 h-8 text-pink-500" />
                Configurações do Webinar
              </h1>
              <p className="text-gray-500 mt-1">Personalize o formulário e as mensagens automáticas</p>
            </div>
            <div className="flex gap-3">
              <Link to={createPageUrl('WebinarInscricao')}>
                <Button variant="outline" className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Ver Página
                </Button>
              </Link>
              <Link to={createPageUrl('Inscritos')}>
                <Button variant="outline" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Ver Inscritos ({inscricoes?.length || 0})
                </Button>
              </Link>
            </div>
          </div>

          <Tabs defaultValue="geral" className="space-y-6">
            <TabsList className="bg-white shadow-sm border p-1 rounded-xl">
              <TabsTrigger value="geral" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white rounded-lg">
                <FileText className="w-4 h-4 mr-2" />
                Informações Gerais
              </TabsTrigger>
              <TabsTrigger value="email" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white rounded-lg">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="data-[state=active]:bg-pink-500 data-[state=active]:text-white rounded-lg">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </TabsTrigger>
            </TabsList>

            <TabsContent value="geral">
              <Card>
                <CardHeader>
                  <CardTitle>Informações do Webinar</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Título do Webinar</Label>
                      <Input
                        value={formData.titulo_webinar}
                        onChange={(e) => handleChange('titulo_webinar', e.target.value)}
                        placeholder="Ex: Inscrição - TalkToMe Partners"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Data do Evento</Label>
                      <Input
                        value={formData.data_evento}
                        onChange={(e) => handleChange('data_evento', e.target.value)}
                        placeholder="Ex: 18 e 19 de dezembro de 2025"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Subtítulo / Descrição Curta</Label>
                    <Input
                      value={formData.subtitulo}
                      onChange={(e) => handleChange('subtitulo', e.target.value)}
                      placeholder="Uma breve descrição do webinar"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Detalhes do Treinamento (uma linha por item)</Label>
                    <Textarea
                      value={formData.detalhes_treinamento}
                      onChange={(e) => handleChange('detalhes_treinamento', e.target.value)}
                      placeholder="Formato: Online Ao Vivo..."
                      className="min-h-[150px]"
                    />
                    <p className="text-sm text-gray-500">Cada linha será exibida como um item separado</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Image className="w-4 h-4" />
                      URL da Logo (para o email)
                    </Label>
                    <Input
                      value={formData.logo_url}
                      onChange={(e) => handleChange('logo_url', e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="email">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-pink-500" />
                    Mensagem de Email
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Assunto do Email</Label>
                    <Input
                      value={formData.assunto_email}
                      onChange={(e) => handleChange('assunto_email', e.target.value)}
                      placeholder="🎉 Inscrição Confirmada..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Corpo do Email (HTML permitido)</Label>
                    <Textarea
                      value={formData.mensagem_email}
                      onChange={(e) => handleChange('mensagem_email', e.target.value)}
                      placeholder="<h2>Parabéns!</h2><p>Sua inscrição...</p>"
                      className="min-h-[200px] font-mono text-sm"
                    />
                    <p className="text-sm text-gray-500">
                      Você pode usar HTML para formatar. O nome do inscrito será automaticamente incluído no início.
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <p className="text-sm font-medium text-gray-700 mb-2">Prévia:</p>
                    <div 
                      className="bg-white rounded-lg p-4 border prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: formData.mensagem_email }}
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="whatsapp">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-green-500" />
                    Mensagem de WhatsApp
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label>Mensagem para o WhatsApp</Label>
                    <Textarea
                      value={formData.mensagem_whatsapp}
                      onChange={(e) => handleChange('mensagem_whatsapp', e.target.value)}
                      placeholder="🎉 Olá! Sua inscrição foi confirmada..."
                      className="min-h-[200px]"
                    />
                    <p className="text-sm text-gray-500">
                      Esta mensagem será usada como template quando o inscrito clicar para entrar no WhatsApp.
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                    <p className="text-sm font-medium text-green-700 mb-2">Prévia do WhatsApp:</p>
                    <div className="bg-white rounded-2xl rounded-tl-sm p-3 shadow-sm max-w-xs">
                      <p className="text-sm whitespace-pre-line">{formData.mensagem_whatsapp}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Save Button */}
          <motion.div 
            className="fixed bottom-6 right-6 z-50"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
          >
            <Button
              onClick={handleSave}
              disabled={saving}
              className={`h-14 px-6 rounded-full shadow-lg transition-all ${
                saved 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600'
              }`}
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : saved ? (
                <CheckCircle className="w-5 h-5 mr-2" />
              ) : (
                <Save className="w-5 h-5 mr-2" />
              )}
              {saving ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar Alterações'}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

